// src/pages/UserAdminPage.tsx
import React, { useEffect, useState } from "react";
import SideMenu from "../../../components/sideMenu";
import DropdownInput from "../../../components/dropdownInput";
import AlertBox from "../../../components/alertBox";
import Loading from "../../../components/loading";
import { organizationService } from "../../../services/organizationService";
import { userService } from "../../../services/userService";
import { uploadFile } from "../../../services/uploadService";
import { AlertConfig, Organization, User, UserRole } from "../../../type";
import { FaCircleArrowLeft } from "react-icons/fa6";
import { useNavigate } from "react-router-dom";
import { roleService } from "../../../services/roleService";
import DropZoneUpload from "../../../components/uploadDropZone";
import { FiAlertTriangle, FiCheckCircle, FiXCircle } from "react-icons/fi";

const PAGE_SIZE = 10;
const PAGE_RANGE = 2;
const ORG_LIMIT = 10;

const UserAdminPage: React.FC = () => {
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [selectedOrgIdx, setSelectedOrgIdx] = useState<number | undefined>();
  const [users, setUsers] = useState<User[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [alertConfig, setAlertConfig] = useState<AlertConfig | null>(null);

  const [profile, setProfile] = useState<User | null>(null);
  const [effective, setEffective] = useState<Record<string, boolean>>({});
  const [isPlatformSuperAdmin, setIsPlatformSuperAdmin] = useState(false);

  const [orgPage, setOrgPage] = useState(1);
  const [totalOrgs, setTotalOrgs] = useState(0);

  // Pagination State
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  // modal & form
  const [openForm, setOpenForm] = useState(false);
  const [editingUser, setEditingUser] = useState<User | null>(null);

  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  // password เดี๋ยวคุณไปออกแบบจริงเองได้ อันนี้เป็น mockup
  const [password, setPassword] = useState("");
  const roles = [UserRole.user, UserRole.admin, UserRole.super_admin];
  const [role, setRole] = useState<number>(0);
  const [selectedRole, setSelectedRole] = useState<UserRole>(roles[0]);
  const [avatarUrl, setAvatarUrl] = useState("");

  const [errors, setErrors] = useState({
    firstName: "",
    lastName: "",
    email: "",
    password: "",
    role: "",
  });

  const validateForm = () => {
    let valid = true;
    const newErrors = {
      firstName: "",
      lastName: "",
      email: "",
      password: "",
      role: "",
    };

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const passwordRegex =
      /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[^A-Za-z0-9]).{8,}$/;

    if (!firstName.trim()) {
      newErrors.firstName = "firstname is required.";
      valid = false;
    }

    if (!lastName.trim()) {
      newErrors.lastName = "lastname is required.";
      valid = false;
    }

    if (!email.trim()) {
      newErrors.email = "email is required.";
      valid = false;
    } else if (!emailRegex.test(email)) {
      newErrors.email = "invalid email format.";
      valid = false;
    }

    if (!editingUser) {
      // CREATE MODE
      if (!password.trim()) {
        newErrors.password = "password is required.";
        valid = false;
      } else if (!passwordRegex.test(password)) {
        newErrors.password =
          "password must contain uppercase, lowercase, number, special character, and be at least 8 characters.";
        valid = false;
      }
    } else {
      // EDIT MODE
      if (password.trim().length > 0 && !passwordRegex.test(password)) {
        newErrors.password =
          "password must contain uppercase, lowercase, number, special character, and be at least 8 characters.";
        valid = false;
      }
    }

    if (!selectedRole) {
      newErrors.role = "role is required.";
      valid = false;
    }

    setErrors(newErrors);
    return valid;
  };

  const navigate = useNavigate();

  const resetForm = () => {
    setEditingUser(null);
    setFirstName("");
    setLastName("");
    setEmail("");
    setPassword("");
    setRole(0);
    setAvatarUrl("");
    setErrors({
      firstName: "",
      lastName: "",
      email: "",
      password: "",
      role: "",
    });
  };

  const openCreateForm = () => {
    resetForm();
    setOpenForm(true);
  };

  const openEditForm = (u: User) => {
    setEditingUser(u);
    setFirstName(u.firstName);
    setLastName(u.lastName);
    setEmail(u.email);
    setPassword(""); // ไม่โชว์ password เดิม
    setRole(roles.findIndex((r) => r == u.role));
    setSelectedRole(u.role);
    setAvatarUrl(u.imageUrl || "");
    setOpenForm(true);
  };

  const fetchOrganizations = async (page = 1, append = false) => {
    setIsLoading(true);
    try {
      const res = await organizationService.getOrganizations({ page, limit: ORG_LIMIT });
      if (append) {
        setOrganizations(prev => [...prev, ...res.data]);
      } else {
        setOrganizations(res.data);
        if (res.data.length > 0) {
          setSelectedOrgIdx(0);
          await fetchUsers(res.data[0].id, 1);
        }
      }
      setTotalOrgs(res.total ?? 0);
    } catch (err) {
      console.error("Failed to fetch organizations", err);
    } finally {
      setIsLoading(false);
    }
  };

  // เพิ่มฟังก์ชันสำหรับโหลดข้อมูลต่อ
  const handleLoadMoreOrgs = () => {
    if (organizations.length < totalOrgs) {
      const nextPage = orgPage + 1;
      setOrgPage(nextPage);
      fetchOrganizations(nextPage, true);
    }
  };

  const fetchUsers = async (orgId: string, page: number = currentPage) => {
    setIsLoading(true);
    try {
      const res = await userService.getUsers({ orgId: orgId, page, limit: PAGE_SIZE });
      setUsers(res.data);
      setTotalPages(Math.ceil((res.total || 0) / PAGE_SIZE));
    } catch (err) {
      console.error("Failed to fetch users", err);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    const init = async () => {
      setIsLoading(true);
      try {
        const [profileRes, effectiveRes] = await Promise.all([
          userService.getProfile(),
          roleService.getMyEffectivePermissions(),
        ]);

        setProfile(profileRes);
        setEffective(effectiveRes.effective || {});

        if (profileRes.isPlatformSuperAdmin) {
          setIsPlatformSuperAdmin(true);
        }
        await fetchOrganizations();
      } catch (err) {
        console.error("[OrganizationAdminPage] init error:", err);
      } finally {
        setIsLoading(false);
      }
    };

    init();
  }, []);

  // UseEffect for Pagination changes
  useEffect(() => {
    if (selectedOrgIdx !== undefined && organizations[selectedOrgIdx]) {
      fetchUsers(organizations[selectedOrgIdx].id, currentPage);
    }
  }, [currentPage]);

  const onSelectOrg = async (idx: number) => {
    setSelectedOrgIdx(idx);
    setCurrentPage(1); // รีเซ็ตหน้าเมื่อเปลี่ยน Org
    const org = organizations[idx];
    if (org) {
      await fetchUsers(org.id, 1);
    }
  };

  const onSelectRole = async (idx: number) => {
    setRole(idx);
    const r = roles[idx];
    if (r) {
      setSelectedRole(r);
    }
  };

  // Pagination Helper
  const getPaginationItems = (current: number, total: number) => {
    const pages: (number | string)[] = [];
    if (total <= 5) {
      for (let i = 1; i <= total; i++) pages.push(i);
    } else {
      pages.push(1);
      if (current > PAGE_RANGE + 2) pages.push("...");
      for (let i = Math.max(2, current - PAGE_RANGE); i <= Math.min(total - 1, current + PAGE_RANGE); i++) {
        pages.push(i);
      }
      if (current < total - PAGE_RANGE - 1) pages.push("...");
      pages.push(total);
    }
    return pages;
  };

  const showSuccessAlert = (title: string, message: string) => {
    setAlertConfig({
      type: "success",
      title,
      message,
    });
  };

  const showErrorAlert = (title: string, message: string) => {
    setAlertConfig({
      type: "error",
      title,
      message,
    });
  };

  const showConfirmDeleteAlert = (userName: string, onConfirm: () => void) => {
    setAlertConfig({
      type: "confirm",
      title: "Confirm deletion",
      message: `Are you sure you want to delete "${userName}"? This action cannot be undone.`,
      onConfirm,
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (selectedOrgIdx === undefined) return;
    const org = organizations[selectedOrgIdx];
    if (!org) return;

    if (!validateForm()) return;

    setIsLoading(true);
    try {
      if (editingUser) {
        await userService.updateUser(editingUser.id, {
          first_name: firstName,
          last_name: lastName,
          email: email.toLowerCase(),
          role: selectedRole,
          img_url: avatarUrl,
          // ถ้าไม่แก้ password อาจไม่ต้องส่ง ให้ backend handle logic เอง
          password: password,
        });
        await fetchUsers(org.id, currentPage);
        setOpenForm(false);
        resetForm();
        showSuccessAlert(
          "Updated successfully",
          `User "${editingUser.firstName} ${editingUser.lastName}" has been updated.`
        );
        localStorage.setItem(
          "user",
          JSON.stringify({
            ...profile,
            firstName,
            lastName,
            email,
            imageUrl: avatarUrl,
          })
        );
        window.dispatchEvent(new Event("user-updated"));
      } else {
        await userService.signup({
          first_name: firstName,
          last_name: lastName,
          email: email.toLowerCase(),
          role: selectedRole!.toString(),
          img_url: avatarUrl || null,
          org_id: org.id,
          password,
        });
        setCurrentPage(1);
        await fetchUsers(org.id, 1);
        setOpenForm(false);
        resetForm();
        showSuccessAlert(
          "Created successfully",
          `user "${firstName} ${lastName}" has been created.`
        );
      }
    } catch (err) {
      console.error("Create/Update user failed", err);
      showErrorAlert(
        "Failed to save user",
        "An error occurred while creating or updating the user. Please try again."
      );
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteUserConfirmed = async (user: User) => {
    setIsLoading(true);
    try {
      await userService.deleteUser(user.id);
      if (selectedOrgIdx !== undefined) {
        const org = organizations[selectedOrgIdx];
        if (org) await fetchUsers(org.id, currentPage);
      }
      showSuccessAlert(
        "Deleted successfully",
        `User "${user.firstName} ${user.lastName}" has been deleted.`
      );
    } catch (err) {
      console.error("Delete user failed", err);
      showErrorAlert(
        "Failed to delete user",
        "An error occurred while deleting the user. Please try again."
      );
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteUser = (user: User) => {
    showConfirmDeleteAlert(`${user.firstName} ${user.lastName}`, () =>
      handleDeleteUserConfirmed(user)
    );
  };

  const canCreateUser = effective["user.create"] || false;
  const canUpdateUser = effective["user.update"] || false;
  const canDeleteUser = effective["user.delete"] || false;

  return (
    <SideMenu active={3}>
      <div className="relative">
        {isLoading && <Loading />}

        <div className="flex">
          <div className="container p-4 pt-8 xl:pt-4">
            <div className="flex md:flex-row flex-col gap-4 items-start md:items-center justify-between mb-4">
              <div className="flex gap-4 items-center">
                <FaCircleArrowLeft
                  className="text-2xl cursor-pointer"
                  onClick={() => navigate(-1)}
                />
                <h3 className="text-xl font-bold">User Management</h3>
              </div>
              {canCreateUser && (
                <button
                  className="px-4 py-2 rounded-lg bg-navy text-white text-sm w-full md:w-36"
                  onClick={openCreateForm}
                >
                  + New User
                </button>
              )}
            </div>

            {/* Org Selector */}
            {isPlatformSuperAdmin && (
              <div className="flex md:flex-row flex-col items-start md:items-center justify-between mb-6">
                <p className="text-sm text-gray-600">
                  Select organization to manage its users.
                </p>
                {organizations.length > 0 && selectedOrgIdx !== undefined && (
                  <div className="md:w-64 w-full">
                    <DropdownInput
                      options={organizations.map((o) => o.orgName)}
                      value_idx={selectedOrgIdx}
                      onSelect={(e) => {
                        onSelectOrg(e);
                      }}
                      onReachBottom={handleLoadMoreOrgs}
                    />
                  </div>
                )}
              </div>
            )}

            {/* Users Table */}
            <div className="border rounded-xl w-[390px] md:w-full overflow-x-auto no-scrollbar text-sm bg-white">
              <div className="w-[900px] md:w-full">
                <div className="grid grid-cols-7 bg-navy text-white p-3 font-semibold">
                  <div className="col-span-2">User</div>
                  <div className="col-span-2">Email</div>
                  <div>Role</div>
                  <div>Created At</div>
                  {(canUpdateUser || canDeleteUser) && (
                    <div className="text-center">Actions</div>
                  )}
                </div>

                <div className="max-h-[500px] overflow-y-auto">
                  {users.map((u, idx) => (
                    <div
                      key={u.id}
                      className={`grid grid-cols-7 items-center p-3 border-b border-gray-100 ${
                        idx % 2 === 0 ? "bg-white" : "bg-gray-50"
                      }`}
                    >
                      <div className="col-span-2 flex items-center gap-3">
                        {u.imageUrl ? (
                          <img
                            src={u.imageUrl}
                            alt="avatar"
                            className="w-10 h-10 rounded-full object-cover border"
                          />
                        ) : (
                          <div className="w-10 h-10 rounded-full bg-gray-300 flex items-center justify-center text-xs font-semibold text-gray-700">
                            {u.firstName.charAt(0)}
                            {u.lastName.charAt(0)}
                          </div>
                        )}
                        <div>
                          <p className="font-medium">
                            {u.firstName} {u.lastName}
                          </p>
                          <p className="text-xs text-gray-500">
                            {u.organization?.orgName}
                          </p>
                        </div>
                      </div>

                      <div className="truncate col-span-2">{u.email}</div>
                      <div className="capitalize">{u.role.replace("_", " ")}</div>
                      <div>
                        {u.createdAt
                          ? new Date(u.createdAt).toLocaleDateString()
                          : "-"}
                      </div>
                      {(canUpdateUser || canDeleteUser) && (
                        <div className="flex justify-center gap-2">
                          {canUpdateUser && (
                            <button
                              className="px-2 py-1 text-xs rounded bg-gray-200"
                              onClick={() => openEditForm(u)}
                            >
                              Edit
                            </button>
                          )}
                          {canDeleteUser && (
                            <button
                              className="px-2 py-1 text-xs rounded bg-red text-white"
                              onClick={() => handleDeleteUser(u)}
                            >
                              Delete
                            </button>
                          )}
                        </div>
                      )}
                    </div>
                  ))}

                  {users.length === 0 && (
                    <div className="p-10 text-center text-gray-500">
                      No users found.
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Pagination UI for Users Table */}
            {totalPages > 1 && (
              <div className="flex flex-row items-center justify-center gap-3 w-full mt-6">
                <button onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))} disabled={currentPage === 1}>
                  <img src="./right-arrow.svg" alt="Prev" className="w-5 rotate-180 opacity-80" />
                </button>
                <div className="flex space-x-2">
                  {getPaginationItems(currentPage, totalPages).map((page, i) => (
                    <button
                      key={i}
                      className={`px-3 py-1 rounded text-sm ${currentPage === page ? "bg-green text-white" : "bg-gray-200 text-black"} ${page === "..." ? "cursor-default" : ""}`}
                      onClick={() => typeof page === "number" && setCurrentPage(page)}
                      disabled={page === "..."}
                    >
                      {page}
                    </button>
                  ))}
                </div>
                <button onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))} disabled={currentPage === totalPages}>
                  <img src="./right-arrow.svg" alt="Next" className="w-5 opacity-80" />
                </button>
              </div>
            )}

          </div>

          {/* User Form Modal */}
          <AlertBox open={openForm}>
            <div className="w-[330px] md:w-[480px]">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-semibold">
                  {editingUser ? "Edit User" : "Create User"}
                </h3>
                <button onClick={() => setOpenForm(false)}>✕</button>
              </div>

              <form className="flex flex-col gap-4" onSubmit={handleSubmit}>
                <div className="flex gap-2">
                  <div className="w-1/2">
                    <label className="block text-sm mb-1">First Name</label>
                    <input
                      className={`w-full border rounded px-3 py-2 text-sm ${
                        errors.firstName ? "border-red" : "border-gray-300"
                      }`}
                      value={firstName}
                      onChange={(e) => {
                        setFirstName(e.target.value);
                        if (errors.firstName) validateForm();
                      }}
                    />
                    {errors.firstName && (
                      <p className="text-xs text-red mt-1">
                        {errors.firstName}
                      </p>
                    )}
                  </div>
                  <div className="w-1/2">
                    <label className="block text-sm mb-1">Last Name</label>
                    <input
                      className={`w-full border rounded px-3 py-2 text-sm ${
                        errors.lastName ? "border-red" : "border-gray-300"
                      }`}
                      value={lastName}
                      onChange={(e) => {
                        setLastName(e.target.value);
                        if (errors.lastName) validateForm();
                      }}
                    />
                    {errors.lastName && (
                      <p className="text-xs text-red mt-1">{errors.lastName}</p>
                    )}
                  </div>
                </div>

                <div>
                  <label className="block text-sm mb-1">Email</label>
                  <input
                    type="email"
                    className={`w-full border rounded px-3 py-2 text-sm ${
                      errors.email ? "border-red" : "border-gray-300"
                    }`}
                    value={email}
                    onChange={(e) => {
                      setEmail(e.target.value);
                      if (errors.email) validateForm();
                    }}
                  />
                  {errors.email && (
                    <p className="text-xs text-red mt-1">{errors.email}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm mb-1">
                    Password{" "}
                    <span className="text-xs text-gray-400">
                      (leave blank if no change)
                    </span>
                  </label>
                  <input
                    type="password"
                    className={`w-full border rounded px-3 py-2 text-sm ${
                      errors.password ? "border-red" : "border-gray-300"
                    }`}
                    value={password}
                    onChange={(e) => {
                      setPassword(e.target.value);
                      if (errors.password) validateForm();
                    }}
                  />
                  {errors.password && (
                    <p className="text-xs text-red mt-1">{errors.password}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm mb-1">Role</label>
                  <DropdownInput
                    options={["User", "Admin", "Super Admin"]}
                    value_idx={role}
                    onSelect={(e) => onSelectRole(e)}
                  />
                </div>

                <DropZoneUpload
                  label="User Avatar"
                  value={avatarUrl}
                  onChange={setAvatarUrl}
                  height={150}
                  circle={false}
                />

                <div className="flex justify-end gap-2 mt-4">
                  <button
                    type="button"
                    className="px-4 py-2 text-sm rounded border"
                    onClick={() => {
                      setOpenForm(false);
                      resetForm();
                    }}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={
                      !!errors.firstName ||
                      !!errors.lastName ||
                      !!errors.email ||
                      !!errors.password ||
                      !!errors.role
                    }
                    className={`px-4 py-2 text-sm rounded text-white
                    ${
                      !!errors.firstName ||
                      !!errors.lastName ||
                      !!errors.email ||
                      !!errors.password ||
                      !!errors.role
                        ? "bg-gray-light cursor-not-allowed"
                        : "bg-navy"
                    }`}
                  >
                    {editingUser ? "Update" : "Create"}
                  </button>
                </div>
              </form>
            </div>
          </AlertBox>
          <AlertBox open={!!alertConfig}>
            {alertConfig && (
              <div className="w-[320px] mdw-[360px]">
                <div className="flex items-center gap-3 mb-3">
                  <div>
                    {alertConfig.type === "success" && (
                      <FiCheckCircle className="text-green text-2xl" />
                    )}
                    {alertConfig.type === "error" && (
                      <FiXCircle className="text-red text-2xl" />
                    )}
                    {alertConfig.type === "confirm" && (
                      <FiAlertTriangle className="text-yellow text-2xl" />
                    )}
                  </div>
                  <h3 className="text-lg font-semibold">{alertConfig.title}</h3>
                </div>

                <p className="text-sm text-gray-700 mb-4 whitespace-pre-line">
                  {alertConfig.message}
                </p>

                <div className="flex justify-end gap-2">
                  {alertConfig.type === "confirm" ? (
                    <>
                      <button
                        className="px-4 py-2 text-sm rounded border"
                        onClick={() => setAlertConfig(null)}
                      >
                        Cancel
                      </button>
                      <button
                        className="px-4 py-2 text-sm rounded bg-red text-white"
                        onClick={() => {
                          alertConfig.onConfirm?.();
                          setAlertConfig(null);
                        }}
                      >
                        Delete
                      </button>
                    </>
                  ) : (
                    <button
                      className="px-4 py-2 text-sm rounded bg-navy text-white"
                      onClick={() => setAlertConfig(null)}
                    >
                      OK
                    </button>
                  )}
                </div>
              </div>
            )}
          </AlertBox>
        </div>
      </div>
    </SideMenu>
  );
};

export default UserAdminPage;